import React from 'react';
import styles from './admission-section-list.module.scss';
import { Loader } from 'src/components/core';
import _ from 'lodash';
import { Lang, ROUTES, CATEGORY_TYPES, INTERNAL_ROUTING } from 'shared/constants';
import update from 'immutability-helper';
import { useNavigate } from 'react-router-dom';
import { AdmissionSection } from '../admission-section';
import { scrollTo } from 'src/utils';
import { ActionButton, NotFound } from 'src/components/common';
import { AdmissionCategoryEditor, AdmissionCategoryDelete } from '../../admission-category';
import { AdmissionGenerateReport } from '../admission-generate-report';
import { AddSectionButton } from 'src/components/common/add-section';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBroomWide, faFileSignature } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from 'shared/constants';

const AdmissionSectionListComponent = React.memo(function AdmissionSectionListComponent({
  adminMode,
  list = null,
  isReady = true,
  fullPath = null,
  lang = Lang.CONTENT_LIST,
  deleteCategory,
  updateCategorySubcategories = () => {},
  admissionSelectChecklist = () => {},
  isHaveActiveChecklist = false,
  ownerIds = [],
  resetChecklist = () => {},
  id = null,
  tags = {},
  isUserIdentified = false,
}) {
  const [categoryEntity, setCategory] = React.useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = React.useState(null);
  const [sections, setSections] = React.useState(list);
  const [isGeneratePDF, setGeneratePDF] = React.useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const checklistId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST);

  React.useEffect(() => {
    setSections(list);
  }, [list]);

  const navigate = useNavigate();

  const generatePdf = async () => {
    setGeneratePDF(true);
    analyticsService.track(EVENTS.CHECKLISTS.CREATE_REPORT, {
      checklistId: checklistId,
    });
  };

  const onFinishGeneratePdf = async () => {
    setGeneratePDF(false);
  };

  const changeSectionOrder = (id, atIndex) => {
    const { section, index } = findSection(id);
    setSections(
      update(sections, {
        $splice: [
          [index, 1],
          [atIndex, 0, section],
        ],
      }),
    );
    // Update the server with the new index
    finishDragSection(id, atIndex);
  };

  const findSection = id => {
    const section = sections.find(i => i.id === id);
    return {
      section,
      index: sections.indexOf(section),
    };
  };

  const finishDragSection = (categoryId, index) => {
    setTimeout(() => scrollTo(categoryId), 300);
    updateCategorySubcategories({
      id,
      categoryId,
      index,
    });
  };

  const createCategoryAction = React.useCallback(
    ({ path, isSectionCreation = false, ownerIds, tags }) => {
      // TODO: add analytics
      setCategory({ ownerIds, path, isSectionCreation, tags });
    },
    [],
  );

  const editCategoryAction = React.useCallback(
    ({ id, title, ownerIds, path, isSectionCreation = false, tags, keywords, type }) => {
      // TODO: add analytics

      setCategory({ id, title, ownerIds, path, isSectionCreation, tags, keywords, type });
    },
    [],
  );

  const deleteCategoryAction = React.useCallback(({ id, title, type, path, ownerIds }) => {
    setDeleteConfirmation({ id, title, type, path, ownerIds });
  }, []);

  const createSection = () => {
    // TODO: analytics
    createCategoryAction({ path: fullPath, isSectionCreation: true, ownerIds, tags });
  };

  const deleteFinish = React.useCallback(() => setDeleteConfirmation(null), []);
  const editorFinish = React.useCallback(
    ({ id, type, isSectionCreation = false, isNew = false }) => {
      setCategory(null);
      if (id && !isSectionCreation) {
        if (type === CATEGORY_TYPES.CHECKS) {
          admissionSelectChecklist(id);
        } else if (isNew) {
          navigate(ROUTES.ADMISSION_CATEGORIES.replace(':id', id));
        }
      }
    },
    [navigate, admissionSelectChecklist],
  );

  if (_.isNull(sections) || !isReady) {
    return <Loader />;
  }

  if (sections.length === 0 && !adminMode) {
    return <NotFound />;
  }

  const callToAction = sections.length === 0 && adminMode && !categoryEntity;

  return (
    <div
      className={classNames(styles.root, { [styles.paddingBottom]: adminMode && !callToAction })}>
      {sections.map((section, index) => (
        <AdmissionSection
          createCategory={createCategoryAction}
          deleteCategory={deleteCategoryAction}
          editCategory={editCategoryAction}
          key={`content-item-${section.id}`}
          firstItem={index === 0}
          lastItem={index === sections.length - 1}
          index={index}
          changeSectionOrder={changeSectionOrder}
          section={section}
          sectionsLength={sections.length}
        />
      ))}

      {callToAction && <AddSectionButton onClick={createSection} />}
      {adminMode && !callToAction && (
        <ActionButton id="admission-action-button" onClick={createSection} />
      )}
      {!adminMode && isUserIdentified && (
        <>
          <ActionButton
            id="admission-action-button"
            onClick={generatePdf}
            renderIcon={
              <FontAwesomeIcon className={styles.actionIcon} icon={faFileSignature} size="xl" />
            }
            className={styles.generatePdf}
            disabled={!isHaveActiveChecklist || isGeneratePDF}
            labelKey="DOWNLOAD"
          />
          <ActionButton
            onClick={resetChecklist}
            className={styles.resetPDF}
            iconClassName={styles.resetPDFIcon}
            disabled={!isHaveActiveChecklist || isGeneratePDF}
            labelKey="RESET"
            renderIcon={
              <FontAwesomeIcon className={styles.actionIcon} icon={faBroomWide} size="xl" />
            }
          />
        </>
      )}

      <AdmissionCategoryEditor
        open={!!categoryEntity}
        onFinish={editorFinish}
        entity={categoryEntity}
      />

      <AdmissionGenerateReport open={isGeneratePDF} onFinish={onFinishGeneratePdf} />

      <AdmissionCategoryDelete
        isOpen={!!deleteConfirmation}
        entity={deleteConfirmation}
        deleteCategory={deleteCategory}
        onFinish={deleteFinish}
      />
    </div>
  );
});

export { AdmissionSectionListComponent };
